import React, { useCallback, useEffect, useState } from 'react';
import HBLinkButton from '../../HBLinkButton/HBLinkButton';
import { Plus16 } from '@honeybook/icons';
import { TFilterField } from '../HBFilters.types';
import HBText from '../../HBText/HBText';
import PopoverBase, { StyledFilterItem, ListItem } from './PopoverBase';
import { flushSync } from 'react-dom';
import { useFilters } from '../hooks/useFilters';
import { STRUCTURE } from '../utils/structure';

interface IAddFilterMenuProps {
    availableFields: TFilterField[];
    disabled?: boolean;
}

interface IUseBeautifulMenuCloseProps {
    availableFields: TFilterField[];
}

const useBeautifulMenuClose = ({
    availableFields
}: IUseBeautifulMenuCloseProps) => {
    const { addFilter } = useFilters();
    const [disabled, setDisabled] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [shouldDisableAnimations, setShouldDisableAnimations] =
        useState(false);

    useEffect(() => {
        if (!availableFields.length) {
            setDisabled(true);
            setIsOpen(false);
        } else {
            setDisabled(false);
        }
    }, [availableFields]);

    const onOpenChange = useCallback(
        ({ isOpen: newIsOpen }: { isOpen: boolean }) => {
            setIsOpen(newIsOpen);
        },
        [setIsOpen]
    );

    const handleFieldAdd = useCallback(
        (field: TFilterField) => {
            // Immediately close the popover
            flushSync(() => {
                setShouldDisableAnimations(true);
            });
            flushSync(() => {
                setIsOpen(false);
            });

            // And only then add the field
            addFilter(field);
        },
        [setShouldDisableAnimations, addFilter, setIsOpen]
    );

    // Reset animation state when popover closes
    useEffect(() => {
        if (!isOpen) {
            setShouldDisableAnimations(false);
        }
    }, [isOpen]);

    return {
        onOpenChange,
        disabled,
        isOpen,
        shouldDisableAnimations,
        handleFieldAdd
    };
};

interface IAddFilterButtonProps {
    disabled: boolean;
}

const AddFilterButton = ({ disabled }: IAddFilterButtonProps) => {
    const { texts } = useFilters();
    return (
        <StyledFilterItem>
            <HBLinkButton
                testIdName="add-filter"
                disabled={disabled}
                icon={<Plus16 />}
                size="sm"
                linkButtonTheme={'neutral'}
            >
                {texts.addFilter}
            </HBLinkButton>
        </StyledFilterItem>
    );
};

const AddFilterMenu = ({ availableFields }: IAddFilterMenuProps) => {
    const { texts } = useFilters();
    const {
        disabled,
        isOpen,
        shouldDisableAnimations,
        handleFieldAdd,
        onOpenChange
    } = useBeautifulMenuClose({ availableFields });

    return (
        <PopoverBase
            targetContainerStyle={disabled ? { pointerEvents: 'none' } : null}
            customStyle={{ maxWidth: STRUCTURE.popoverWidth }}
            target={<AddFilterButton disabled={disabled} />}
            title={texts.selectFilter}
            testIdName="add-filter"
            onOpenChange={onOpenChange}
            forceClose={!isOpen}
            forceOpen={isOpen}
            disableAnimations={shouldDisableAnimations}
        >
            {availableFields.map(field => (
                <ListItem
                    testId="add-filter-list-item"
                    $alignment={'left'}
                    key={field.id}
                    onClick={() => handleFieldAdd(field)}
                    size="md"
                    rounded
                >
                    {field.icon}
                    <HBText type={'body14'}>{field.label}</HBText>
                </ListItem>
            ))}
        </PopoverBase>
    );
};

export default AddFilterMenu;
